import { Suspense } from 'react';
import { OverviewTab } from './ClientProfilePanel/OverviewTab';
import { LeadOriginSection } from './ClientProfilePanel/LeadOriginSection';
import { ClientVisitsTab } from './ClientProfilePanel/ClientVisitsTab';
import { ClientPropertiesInterestTab } from './ClientProfilePanel/ClientPropertiesInterestTab';
import { ClientDocumentsPanel } from './ClientProfilePanel/ClientDocumentsPanel';
import { ClientHistoryTab } from './ClientProfilePanel/ClientHistoryTab';
import { ClientProposalsTab } from './ClientProfilePanel/ClientProposalsTab';
import { FF_APP_CREDIT_RISK } from '@/shared/utils/feature-flag';
import { RiskScorePanel } from './ClientRiskScore/RiskScorePanel.lazy';
import type { ClientRow } from '../context/ClientManagementContext';
import type { ClientDetailTab } from './ClientDetailView/client-detail-tabs';

export interface ClientProfileContentProps {
  client: ClientRow;
  agencyId: string;
  activeTab: ClientDetailTab;
  onMerged?: () => void;
}

/**
 * The active client-detail tab's panel.
 *
 * The layout around it — header, the two bands, and the right rail —
 * belongs to `ClientDetailShell`, so this renders one panel at full
 * column width and nothing else. The Social chat launcher renders
 * unconditionally rather than under `overview`, so it stays visible
 * while switching between this client's sub-tabs.
 */
export function ClientProfileContent({
  client,
  agencyId,
  activeTab,
  onMerged,
}: ClientProfileContentProps) {
  return (
    <div className="flex min-w-0 flex-col">
      <LeadOriginSection
        lead_origin={client.lead_origin}
        lead_interactions={client.lead_interactions}
        contacts={client.contacts}
        agencyId={agencyId}
        clientId={client.client_id}
        clientName={client.full_name}
        onMerged={onMerged}
      />

      {activeTab === 'overview' && (
        <OverviewTab client={client} agencyId={agencyId} />
      )}
      {activeTab === 'visits' && (
        <ClientVisitsTab clientId={client.client_id} agencyId={agencyId} />
      )}
      {activeTab === 'propertiesOfInterest' && (
        <ClientPropertiesInterestTab client={client} agencyId={agencyId} />
      )}
      {activeTab === 'documents' && (
        <ClientDocumentsPanel clientId={client.client_id} agencyId={agencyId} />
      )}
      {activeTab === 'history' && (
        <ClientHistoryTab clientId={client.client_id} agencyId={agencyId} />
      )}
      {activeTab === 'proposals' && (
        <ClientProposalsTab client={client} agencyId={agencyId} />
      )}
      {activeTab === 'risk' && FF_APP_CREDIT_RISK && (
        <Suspense fallback={null}>
          <RiskScorePanel
            agencyId={agencyId}
            clientId={client.client_id}
            clientDocument={client.cpf || client.cnpj}
          />
        </Suspense>
      )}
    </div>
  );
}
